import { withDependencies, named } from '@wix/thunderbolt-ioc'
import type { IPageWillMountHandler, IPageWillUnmountHandler } from '@wix/thunderbolt-symbols'
import { PageFeatureConfigSymbol, SiteFeatureConfigSymbol, LoggerSymbol } from '@wix/thunderbolt-symbols'
import type { ILogger } from '@wix/thunderbolt-types'
import type { IBuilderComponentPageConfig, BuilderComponentSiteConfig } from './types'
import { supportsContainerStyleQueries } from '@wix/thunderbolt-commons'
import { createPolyfillDataWithComponentId } from './utils'
import { builderComponentPolyfillResizeObserver } from './presetsPolyfillResizeObserver'
import { name } from './symbols'

export const BuilderComponentPresetsPolyfillPageLifecycle = withDependencies(
	[named(SiteFeatureConfigSymbol, name), named(PageFeatureConfigSymbol, name), LoggerSymbol],
	(
		siteConfig: BuilderComponentSiteConfig,
		pageConfig: IBuilderComponentPageConfig,
		logger: ILogger
	): IPageWillMountHandler & IPageWillUnmountHandler => {
		// Store the observer instance to clean it up later
		let resizeObserver: ReturnType<typeof builderComponentPolyfillResizeObserver> | null = null

		// Create a getter function that computes polyfill data on-demand
		// This ensures we always read fresh data from configs (which may be updated byRef)
		const getPresetsPolyfillData = () => {
			return pageConfig.pagePolyfillData && siteConfig.builderComponentsData
				? createPolyfillDataWithComponentId(pageConfig.pagePolyfillData, siteConfig.builderComponentsData)
				: {}
		}

		return {
			name: 'BuilderComponentPresetsPolyfillPageLifecycle',
			pageWillMount: async () => {
				if (!pageConfig.pagePolyfillData || !siteConfig.builderComponentsData || supportsContainerStyleQueries()) {
					return
				}

				try {
					// Pass the getter function and logger so ResizeObserver can measure performance
					resizeObserver = builderComponentPolyfillResizeObserver(getPresetsPolyfillData, logger)
				} catch (error) {
					logger.captureError(error as Error, {
						tags: { feature: 'builder-component', lifecycle: 'pageWillMount' },
					})
				}
			},
			pageWillUnmount: async (pageInfo: { pageId: string; contextId: string }) => {
				try {
					// Dispose of the observer when page unmounts to prevent memory leaks
					// and ensure each page has isolated polyfill data
					if (resizeObserver) {
						resizeObserver.dispose()
						resizeObserver = null
					}
				} catch (error) {
					logger.captureError(error as Error, {
						tags: { feature: 'builder-component', lifecycle: 'pageWillUnmount' },
					})
				}
			},
		}
	}
)
